import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AlertTriangle, BellRing, FilePlus2 } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardContent } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { Skeleton } from '@/components/ui/Skeleton'
import { ChannelBadge } from '@/features/transactions/components/ChannelBadge'
import { formatDateTime, formatTimeAgo } from '@/lib/format'
import { useCan } from '@/lib/permissions'
import type { UnlinkedAlertRow } from '../api'
import {
  useCreateCaseFromAlert,
  useUnlinkedAlerts,
} from '../hooks/useUnlinkedAlerts'

const PRIORITY_VARIANT: Record<
  UnlinkedAlertRow['priority'],
  React.ComponentProps<typeof Badge>['variant']
> = {
  critical: 'critical',
  high: 'failed',
  medium: 'pending',
  low: 'default',
}

/** Permission enforced server-side on POST /alerts/bulk/link-to-case. */
const CREATE_CASE_PERMISSION = 'cases:write'

interface PartialFailure {
  alertId: string
  alertNumber: string
  caseId: string
}

/**
 * Recent alerts that have no case. Alerts and cases are intentionally
 * separate: cases are opened by analysts from alerts, never automatically.
 * This panel keeps freshly raised alerts discoverable from the Cases page.
 */
export function UnlinkedAlertsPanel() {
  const navigate = useNavigate()
  const canCreate = useCan(CREATE_CASE_PERMISSION)
  const query = useUnlinkedAlerts()
  const create = useCreateCaseFromAlert()
  const [partial, setPartial] = useState<PartialFailure | null>(null)
  const [rowError, setRowError] = useState<{ alertId: string; message: string } | null>(
    null,
  )

  const busy = create.isPending

  function createFrom(alert: UnlinkedAlertRow) {
    if (busy) return
    setPartial(null)
    setRowError(null)
    create.mutate(
      {
        alert_ids: [alert.id],
        case_id: null,
        new_case_title: `${alert.alert_number}${alert.scenario_name ? ` · ${alert.scenario_name}` : ''}`,
        new_case_priority: alert.priority,
      },
      {
        onSuccess: (data) => {
          if (data.updated >= 1) {
            void navigate(`/cases/${data.case_id}`)
          } else {
            // Case exists but the alert was not attached: surface it.
            setPartial({
              alertId: alert.id,
              alertNumber: alert.alert_number,
              caseId: data.case_id,
            })
          }
        },
        onError: () =>
          setRowError({
            alertId: alert.id,
            message: `Couldn't create a case for ${alert.alert_number}. No case was created; try again.`,
          }),
      },
    )
  }

  function retryLink(p: PartialFailure) {
    if (busy) return
    create.mutate(
      { alert_ids: [p.alertId], case_id: p.caseId },
      {
        onSuccess: (data) => {
          if (data.updated >= 1) {
            setPartial(null)
            void navigate(`/cases/${data.case_id}`)
          }
        },
        onError: () => undefined, // partial banner stays; retry remains available
      },
    )
  }

  const alerts = query.data?.alerts ?? []

  return (
    <section aria-labelledby="unlinked-alerts-heading" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 id="unlinked-alerts-heading" className="text-sm font-semibold">
            Recent unlinked alerts
          </h2>
          <p className="text-xs text-muted-fg">
            Cases are created by analysts from alerts; they are not opened
            automatically. These recent alerts are not part of any case yet.{' '}
            <Link to="/alerts" className="text-primary underline-offset-2 hover:underline">
              Open the Alerts queue
            </Link>
            .
          </p>
        </div>
        {query.data && query.data.total > alerts.length && (
          <span className="text-xs text-muted-fg">
            Showing {alerts.length} of {query.data.total.toLocaleString()}
          </span>
        )}
      </div>

      {partial && (
        <div
          role="alert"
          className="flex flex-wrap items-center gap-3 rounded-md border border-status-failed/30 bg-status-failed/5 p-3 text-sm text-status-failed"
        >
          <AlertTriangle size={14} aria-hidden />
          <span className="flex-1">
            A case was created but alert {partial.alertNumber} was not linked to
            it. The alert is still unlinked.
          </span>
          <Link
            to={`/cases/${partial.caseId}`}
            className="text-xs underline underline-offset-2"
          >
            View case
          </Link>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => retryLink(partial)}>
            {busy ? 'Retrying…' : 'Retry link'}
          </Button>
        </div>
      )}

      {query.isLoading ? (
        <div className="space-y-2" aria-busy="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <div
          role="alert"
          className="flex items-center justify-between gap-3 rounded-md border border-status-failed/30 bg-status-failed/5 p-3 text-sm text-status-failed"
        >
          <span>Couldn't load recent unlinked alerts.</span>
          <Button size="sm" variant="outline" onClick={() => void query.refetch()}>
            Retry
          </Button>
        </div>
      ) : alerts.length === 0 ? (
        <EmptyState
          compact
          icon={BellRing}
          title="No unlinked alerts"
          description="Every recent open alert is already part of a case."
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <caption className="sr-only">
                  Recent open alerts not linked to a case, newest first
                </caption>
                <thead className="border-b border-border bg-muted/40 text-[0.625rem] uppercase tracking-wider text-muted-fg">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left">Alert #</th>
                    <th scope="col" className="px-3 py-2 text-left">Created</th>
                    <th scope="col" className="px-3 py-2 text-left">Priority</th>
                    <th scope="col" className="px-3 py-2 text-left">Channel</th>
                    <th scope="col" className="px-3 py-2 text-left">Customer</th>
                    <th scope="col" className="px-3 py-2 text-right">Score</th>
                    {canCreate && (
                      <th scope="col" className="px-3 py-2 text-right">
                        <span className="sr-only">Actions</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {alerts.map((a) => (
                    <tr key={a.id} className="border-b border-border last:border-0">
                      <td className="px-3 py-2 font-mono">
                        <Link
                          to={`/alerts/${a.id}`}
                          className="text-primary hover:underline"
                        >
                          {a.alert_number}
                        </Link>
                      </td>
                      <td className="px-3 py-2" title={formatDateTime(a.created_at)}>
                        {formatTimeAgo(a.created_at)}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant={PRIORITY_VARIANT[a.priority] ?? 'default'}>
                          {a.priority}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        <ChannelBadge channel={a.channel} />
                      </td>
                      <td className="px-3 py-2">
                        {a.customer?.display_name || a.customer?.customer_number || '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{a.score}</td>
                      {canCreate && (
                        <td className="px-3 py-2 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busy}
                            aria-label={`Create case from alert ${a.alert_number}`}
                            onClick={() => createFrom(a)}
                          >
                            <FilePlus2 size={12} className="mr-1" aria-hidden />
                            Create case
                          </Button>
                          {rowError?.alertId === a.id && (
                            <p role="alert" className="mt-1 text-[0.6875rem] text-status-failed">
                              {rowError.message}
                            </p>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </section>
  )
}
